import type { ReactNode } from 'react';
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './Toast.module.css';

/* ── Types ── */

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  exiting: boolean;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType, duration?: number) => void;
}

/* ── Context ── */

const ToastContext = createContext<ToastContextValue | null>(null);

/* ── Provider ── */

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const removeToast = useCallback((id: number) => {
    // Mark as exiting for animation
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
    );
    // Remove after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 200);
  }, []);

  const toast = useCallback(
    (message: string, type: ToastType = 'info', duration = 5000) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, message, type, exiting: false }]);

      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {createPortal(
        <div className={styles.container} aria-live="polite">
          {toasts.map((t) => (
            <ToastMessage
              key={t.id}
              item={t}
              onClose={() => removeToast(t.id)}
            />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

/* ── Hook ── */

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

/* ── Toast message component ── */

interface ToastMessageProps {
  item: ToastItem;
  onClose: () => void;
}

function ToastMessage({ item, onClose }: ToastMessageProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Auto-mark as entered after mount (for animation)
  useEffect(() => {
    // Force reflow so the enter animation plays
    ref.current?.getBoundingClientRect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${styles.toast} ${styles[item.type]} ${item.exiting ? styles.exit : styles.enter}`}
      role="alert"
    >
      <span className={styles.icon} aria-hidden="true">
        {iconForType(item.type)}
      </span>
      <span className={styles.message}>{item.message}</span>
      <button
        className={styles.close}
        onClick={onClose}
        aria-label="Dismiss notification"
        type="button"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M12 4L4 12M4 4l8 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

function iconForType(type: ToastType): string {
  switch (type) {
    case 'success':
      return '\u2713';
    case 'error':
      return '\u2717';
    case 'warning':
      return '\u26A0';
    case 'info':
      return '\u2139';
  }
}
